/**
 * Analytics utility
 *
 * Thin wrapper around PostHog. All calls are silent no-ops if PostHog has not
 * been initialized (i.e. REACT_APP_POSTHOG_KEY is not set or is placeholder).
 *
 * Usage:
 *   import { track, Events } from '../utils/analytics';
 *   track(Events.ROOM_PUBLISHED, { roomId, slug });
 */

// ─── Helper Functions ─────────────────────────────────────────────────────────

/**
 * Check if PostHog is properly initialized and ready to capture events.
 * @returns {boolean}
 */
function isPostHogEnabled() {
  return (
    typeof window !== 'undefined' &&
    window.posthog &&
    typeof window.posthog.capture === 'function'
  );
}

// ─── Event constants ──────────────────────────────────────────────────────────

export const Events = {
  // ─── Authentication ─────────────────────────────────────────────────────────
  /** User arrives at signup page or clicks sign up */
  SIGN_UP_STARTED: 'sign_up_started',
  /** User arrives at signin page or clicks sign in */
  SIGN_IN_STARTED: 'sign_in_started',
  /** User completes authentication (distinguishes new vs returning via isNewUser) */
  SIGN_IN_COMPLETED: 'sign_in_completed',

  // ─── Room Creation ───────────────────────────────────────────────────────────
  /** User clicks any "Create a Room" button */
  CREATE_ROOM_CLICKED: 'create_room_clicked',
  /** User successfully creates a new room */
  ROOM_CREATED: 'room_created',
  /** User creates their very first room (tracked alongside room_created when roomCount === 1) */
  FIRST_ROOM_CREATED: 'first_room_created',

  // ─── Room Editing ────────────────────────────────────────────────────────────
  /** User places first asset in a room (per room, not per asset) */
  ASSET_PLACED: 'asset_placed',
  /** User adds first decoration to a room (per room, not per decoration) */
  DECORATION_ADDED: 'decoration_added',
  /** User enters player/preview mode from the editor */
  PLAYER_MODE_ENTERED: 'player_mode_entered',
  /** User updates their room's name */
  ROOM_NAME_UPDATED: 'room_name_updated',

  // ─── Publishing ─────────────────────────────────────────────────────────────
  /** User successfully publishes a room */
  ROOM_PUBLISHED: 'room_published',
  /** User attempts to publish but it fails */
  PUBLISH_FAILED: 'publish_failed',

  // ─── Sharing ────────────────────────────────────────────────────────────────
  /** User copies a room share link to clipboard */
  SHARE_LINK_COPIED: 'share_link_copied',
  /** User shares a room (native share or button click) */
  ROOM_SHARED: 'room_shared',

  // ─── Room Consumption ───────────────────────────────────────────────────────
  /** A room is loaded and displayed in the player */
  ROOM_VIEWED: 'room_viewed',
  /** A room fails to load (404, 403, network error, etc.) */
  ROOM_LOAD_FAILED: 'room_load_failed',

  // ─── Discovery ──────────────────────────────────────────────────────────────
  /** User clicks on a room card to view it */
  FEATURED_ROOM_CLICKED: 'featured_room_clicked',
  /** Landing page mounts (hero + rooms section) */
  LANDING_PAGE_VIEWED: 'landing_page_viewed',
  /** User changes tab on the landing page */
  TAB_CHANGED: 'tab_changed',
  /** User opens the About modal */
  ABOUT_MODAL_OPENED: 'about_modal_opened',

  // ─── Retention ─────────────────────────────────────────────────────────────
  /** Owner opens their published room in the editor */
  EDITOR_OPENED: 'editor_opened',
  /** User edits a room that existed before the current session */
  ROOM_EDITED_EXISTING: 'room_edited_existing',

  // ─── Content ───────────────────────────────────────────────────────────────
  /** User visits a creator's profile page */
  CREATOR_PROFILE_PAGE_VIEWED: 'creator_profile_page_viewed',

  // ─── Onboarding ─────────────────────────────────────────────────────────────
  /** User completes the onboarding survey */
  ONBOARDING_SURVEY_COMPLETED: 'onboarding_survey_completed',
  /** User skips the onboarding survey */
  ONBOARDING_SURVEY_SKIPPED: 'onboarding_survey_skipped',

  // ─── Error Signals ──────────────────────────────────────────────────────────
  /** User submits a report on a room */
  ROOM_REPORTED: 'room_reported',

  // ─── Legacy ────────────────────────────────────────────────────────────────
  /** Guest user clicks Sign Up in the session-expiry warning banner */
  GUEST_SIGNUP_CLICKED: 'guest_signup_clicked',
  /** User deletes one of their rooms */
  ROOM_DELETED: 'room_deleted',

  // ─── Pro Creator ──────────────────────────────────────────────────────────
  /** User clicks the Pro Creator button on the landing page */
  PRO_CREATOR_BUTTON_CLICKED: 'pro_creator_button_clicked',
  /** User submits their email to the Pro Creator waitlist */
  PRO_CREATOR_WAITLIST_SUBMITTED: 'pro_creator_waitlist_submitted',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Capture a custom event.
 * Silent no-op if PostHog is not initialised.
 *
 * @param {string} event - One of Events.*
 * @param {object} [properties] - Additional properties to attach
 */
export function track(event, properties = {}) {
  try {
    if (isPostHogEnabled()) {
      window.posthog.capture(event, properties);
    }
  } catch (_) {
    // Ignore — PostHog not yet initialised or key missing
  }
}

/**
 * Capture a custom event with UTM/referrer attribution automatically attached.
 * Use this for all user-facing events to enable marketing attribution.
 *
 * @param {string} event - One of Events.*
 * @param {object} [properties] - Additional properties to attach
 */
export function trackWithAttribution(event, properties = {}) {
  if (typeof window === 'undefined') return;

  try {
    if (!isPostHogEnabled()) return;

    const referrer = document.referrer || '';
    const urlParams = new URLSearchParams(window.location.search);

    window.posthog.capture(event, {
      ...properties,
      referrer,
      utmSource: urlParams.get('utm_source') || null,
      utmMedium: urlParams.get('utm_medium') || null,
      utmCampaign: urlParams.get('utm_campaign') || null,
      timestamp: new Date().toISOString(),
    });
  } catch (_) {
    // Ignore — PostHog not yet initialised or key missing
  }
}

/**
 * Link the current anonymous session to a known user identity.
 * Call once after authentication is confirmed.
 *
 * @param {string} userId - Clerk user.id
 * @param {object} [traits] - Optional user traits (email, name, etc.)
 */
export function identifyUser(userId, traits = {}) {
  try {
    if (isPostHogEnabled()) {
      window.posthog.identify(userId, traits);
    }
  } catch (_) {
    // Ignore
  }
}

/**
 * Capture a pageview event for SPA route changes.
 * Use this in useEffect when location changes.
 */
export function capturePageview() {
  if (typeof window === 'undefined') return;

  try {
    if (isPostHogEnabled()) {
      window.posthog.capture('$pageview', {
        url: window.location.href,
        path: window.location.pathname,
      });
    }
  } catch (_) {
    // Silently fail — never crash the app
  }
}
